import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { decodeJWT } from "../../api/axiosConfig";
import { fetchHospitals } from '../../store/slices/Hospital/Index';
import { createDoctorAppointment, resetDoctorAppointment } from '../../store/slices/Doctor_Appointment/Index';
import Swal from 'sweetalert2';
import {ArrowLeft} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {useAppSelector} from "../../store/hooks";

interface FormData {
  insuranceCardNumber: string;
  name: string;
  surname: string;
  birthDate: string;
  phone: string;
  email: string;
  appealDate: string;
  appealTime: string;
  reasonForAppeal: string;
  wantsHospital: string;
  clinic: string;
  doctorName: string;
}

interface Hospital {
  id: number;
  name: string;
}

interface HospitalsState {
  hospitals: Hospital[];
  loading: boolean;
  error: string | null;
  success: boolean;
}

interface DoctorAppointmentState {
  data: any;
  loading: boolean;
  error: string | null;
  validationErrors: any[] | null;
  success: boolean;
}

interface RootState {
  hospitals: HospitalsState;
  doctorAppointment?: DoctorAppointmentState;
}

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

const Doctor_Appointment: React.FC = () => {
  const dispatch = useDispatch();

  const { hospitals, loading: hospitalsLoading } = useSelector(
    (state: RootState) => state.hospitals
  );

  const { loading: submitting, error, success } = useSelector(
    (state: RootState) => state.doctorAppointment || { loading: false, error: null, validationErrors: null, success: false }
  );
  const navigate = useNavigate();
  const {  loading } = useAppSelector((state) => state.contact);

  const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';


  const translations = {
    az: {
      title: "Həkimə qeydiyyat",
      subtitle: "Həkimə qeydiyyat üçün aşağıdakı məlumatları doldurmağınız xahiş olunur.",
      loading: "Məlumatlar yüklənir",
      name: "Ad",
      surname: "Soyad",
      birthDate: "Doğum tarixi",
      insuranceCardNumber: "Sığorta kartının nömrəsi",
      phone: "Telefon nömrəsi",
      email: "Elektron poçt ünvanı",
      appealDate: "Həkimə müraciət tarixi",
      appealTime: "Həkimə müraciət saatı",
      wantsHospital: "Xəstəxana seçmək istəyirsiniz?",
      select: "Seçin",
      yes: "Bəli",
      no: "Xeyr",
      clinic: "Klinika",
      doctorName: "Həkimin adı",
      reasonForAppeal: "Müraciət səbəbi və şikayət",
      reasonPlaceholder: "Müraciət səbəbinizi ətraflı izah edin...",
      submit: "Göndər",
      submitting: "Göndərilir...",
      back: "geri",
      successTitle: "Uğurlu!",
      successMessage: "Həkimə qeydiyyat uğurla göndərildi!",
      errorTitle: "Xəta!",
      warningTitle: "Diqqət!",
      fillAllFields: "Zəhmət olmasa bütün xanaları doldurun",
      userNotFound: "İstifadəçi məlumatları tapılmadı. Xahiş edirik yenidən giriş edin.",
      ok: "Tamam",
      close: "Bağla",
      doctorNamePlaceholder: "Həkimin adını daxil edin",
      loadingHospitals: "Yüklənir...",
      errors: {
        insuranceCardNumber: "Sığorta kartının nömrəsi tələb olunur",
        name: "Ad tələb olunur",
        surname: "Soyad tələb olunur",
        birthDate: "Doğum tarixi tələb olunur",
        phone: "Telefon nömrəsi tələb olunur",
        phoneInvalid: "Düzgün telefon nömrəsi daxil edin",
        email: "Email tələb olunur",
        emailInvalid: "Düzgün email daxil edin",
        appealDate: "Müraciət tarixi tələb olunur",
        appealTime: "Müraciət saatı tələb olunur",
        reasonForAppeal: "Müraciət səbəbi tələb olunur",
        wantsHospital: "Bu sahəni seçmək tələb olunur",
        clinic: "Klinika seçmək tələb olunur",
        doctorName: "Həkimin adı tələb olunur"
      }
    },
    ru: {
      title: "Запись к врачу",
      subtitle: "Для записи к врачу просим заполнить следующую информацию.",
      loading: "Загрузка данных",
      name: "Имя",
      surname: "Фамилия",
      birthDate: "Дата рождения",
      insuranceCardNumber: "Номер страхового полиса",
      phone: "Номер телефона",
      email: "Адрес электронной почты",
      appealDate: "Дата обращения к врачу",
      appealTime: "Время обращения к врачу",
      wantsHospital: "Хотите выбрать больницу?",
      select: "Выберите",
      yes: "Да",
      no: "Нет",
      clinic: "Клиника",
      doctorName: "Имя врача",
      reasonForAppeal: "Причина обращения и жалоба",
      reasonPlaceholder: "Подробно опишите причину обращения...",
      submit: "Отправить",
      submitting: "Отправка...",
      back: "назад",
      successTitle: "Успешно!",
      successMessage: "Запись к врачу успешно отправлена!",
      errorTitle: "Ошибка!",
      warningTitle: "Внимание!",
      fillAllFields: "Пожалуйста, заполните все поля",
      userNotFound: "Данные пользователя не найдены. Пожалуйста, войдите снова.",
      ok: "Хорошо",
      close: "Закрыть",
      doctorNamePlaceholder: "Введите имя врача",
      loadingHospitals: "Загрузка...",
      errors: {
        insuranceCardNumber: "Номер страхового полиса обязателен",
        name: "Имя обязательно",
        surname: "Фамилия обязательна",
        birthDate: "Дата рождения обязательна",
        phone: "Номер телефона обязателен",
        phoneInvalid: "Введите правильный номер телефона",
        email: "Email обязателен",
        emailInvalid: "Введите правильный email",
        appealDate: "Дата обращения обязательна",
        appealTime: "Время обращения обязательно",
        reasonForAppeal: "Причина обращения обязательна",
        wantsHospital: "Необходимо выбрать это поле",
        clinic: "Необходимо выбрать клинику",
        doctorName: "Имя врача обязательно"
      }
    },
    en: {
      title: "Doctor Appointment",
      subtitle: "Please fill in the following information for doctor appointment.",
      loading: "Loading data",
      name: "Name",
      surname: "Surname",
      birthDate: "Date of birth",
      insuranceCardNumber: "Insurance card number",
      phone: "Phone number",
      email: "Email address",
      appealDate: "Appointment date",
      appealTime: "Appointment time",
      wantsHospital: "Do you want to choose a hospital?",
      select: "Select",
      yes: "Yes",
      no: "No",
      clinic: "Clinic",
      doctorName: "Doctor's name",
      reasonForAppeal: "Reason for visit and complaint",
      reasonPlaceholder: "Explain the reason for your visit in detail...",
      submit: "Submit",
      submitting: "Submitting...",
      back: "back",
      successTitle: "Success!",
      successMessage: "Doctor appointment submitted successfully!",
      errorTitle: "Error!",
      warningTitle: "Attention!",
      fillAllFields: "Please fill in all fields",
      userNotFound: "User information not found. Please login again.",
      ok: "OK",
      close: "Close",
      doctorNamePlaceholder: "Enter doctor's name",
      loadingHospitals: "Loading...",
      errors: {
        insuranceCardNumber: "Insurance card number is required",
        name: "Name is required",
        surname: "Surname is required",
        birthDate: "Date of birth is required",
        phone: "Phone number is required",
        phoneInvalid: "Enter a valid phone number",
        email: "Email is required",
        emailInvalid: "Enter a valid email",
        appealDate: "Appointment date is required",
        appealTime: "Appointment time is required",
        reasonForAppeal: "Reason for visit is required",
        wantsHospital: "This field is required",
        clinic: "Clinic selection is required",
        doctorName: "Doctor's name is required"
      }
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const [userId, setUserId] = useState<number | null>(null);
  const [minDate, setMinDate] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FormData>({
    insuranceCardNumber: '',
    name: '',
    surname: '',
    birthDate: '',
    phone: '',
    email: '',
    appealDate: '',
    appealTime: '',
    reasonForAppeal: '',
    wantsHospital: '',
    clinic: '',
    doctorName: ''
  });

  useEffect(() => {
    dispatch(fetchHospitals({ language: 'az' }) as any);

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setMinDate(`${year}-${month}-${day}`);
  }, [dispatch]);

  useEffect(() => {
    const loadUserData = () => {
      const token = getCookie('token') ||
        getCookie('jwt_token') ||
        getCookie('accessToken') ||
        getCookie('auth_token') ||
        getCookie('jwt');

      if (token) {
        try {
          const decodedData = decodeJWT(token);

          if (decodedData) {
            setFormData(prev => ({
              ...prev,
              insuranceCardNumber: decodedData.medical_card || '',
              name: decodedData.first_name || '',
              surname: decodedData.last_name || '',
              phone: decodedData.phone_number || '',
              email: decodedData.email || '',
              birthDate: decodedData.birth_date || ''
            }));

            if (decodedData.user_id) {
              setUserId(decodedData.user_id);
            }
          }
        } catch (error) {
          console.error('JWT decode error:', error);
        }
      } else {
        console.error('Token cookie-də tapılmadı!');
      }
    };

    loadUserData();
  }, []);

  // Handle success
  useEffect(() => {
    if (success) {
      Swal.fire({
        icon: 'success',
        title: t.successTitle,
        text: t.successMessage,
        confirmButtonText: t.ok,
        confirmButtonColor: '#db2777',
      }).then(() => {
        setFormData({
          insuranceCardNumber: '',
          name: '',
          surname: '',
          birthDate: '',
          phone: '',
          email: '',
          appealDate: '',
          appealTime: '',
          reasonForAppeal: '',
          wantsHospital: '',
          clinic: '',
          doctorName: ''
        });

        setFieldErrors({});

        dispatch(resetDoctorAppointment() as any);
        navigate('/medicalrequests');

      });
    }
  }, [success, dispatch, navigate, t]);

  useEffect(() => {
    if (error) {
      Swal.fire({
        icon: 'error',
        title: t.errorTitle,
        text: error,
        confirmButtonText: t.ok,
        confirmButtonColor: '#db2777',
      });
    }
  }, [error, t]);

  const validateField = (name: string, value: string): string => {
    if (name === 'insuranceCardNumber' && !value.trim()) {
      return t.errors.insuranceCardNumber;
    }
    if (name === 'name' && !value.trim()) {
      return t.errors.name;
    }
    if (name === 'surname' && !value.trim()) {
      return t.errors.surname;
    }
    if (name === 'birthDate' && !value) {
      return t.errors.birthDate;
    }
    if (name === 'phone' && !value.trim()) {
      return t.errors.phone;
    }
    if (name === 'email' && !value.trim()) {
      return t.errors.email;
    }
    if (name === 'appealDate' && !value) {
      return t.errors.appealDate;
    }
    if (name === 'appealTime' && !value) {
      return t.errors.appealTime;
    }
    if (name === 'reasonForAppeal' && !value.trim()) {
      return t.errors.reasonForAppeal;
    }
    if (name === 'wantsHospital' && !value) {
      return t.errors.wantsHospital;
    }

    // Hospital selection validation
    if (formData.wantsHospital === 'beli') {
      if (name === 'clinic' && !value) {
        return t.errors.clinic;
      }
    }

    if (name === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
      return t.errors.emailInvalid;
    }

    if (name === 'phone' && value && !/^[0-9+\s()-]+$/.test(value)) {
      return t.errors.phoneInvalid;
    }

    return '';
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    const error = validateField(name, value);
    setFieldErrors(prev => {
      if (error) {
        return { ...prev, [name]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Special handling for phone input
    if (name === 'phone') {
      let phoneValue = value;

      if (phoneValue === "") {
        setFormData(prev => ({
          ...prev,
          phone: "+994 "
        }));
        return;
      }

      if (!phoneValue.startsWith("+994 ")) {
        setFormData(prev => ({
          ...prev,
          phone: "+994 "
        }));
        return;
      }

      const digitsOnly = phoneValue.slice(5).replace(/\D/g, '');

      if (digitsOnly.length <= 9) {
        setFormData(prev => ({
          ...prev,
          phone: `+994 ${digitsOnly}`
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ...(name === 'wantsHospital' && value === 'xeyr' ? { clinic: '', doctorName: '' } : {})
      }));
    }

    if (fieldErrors[name]) {
      const error = validateField(name, name === 'phone' ? (value.startsWith('+994 ') ? `+994 ${value.slice(5).replace(/\D/g, '')}` : value) : value);
      setFieldErrors(prev => {
        if (error) {
          return { ...prev, [name]: error };
        } else {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        }
      });
    }
  };
  const getInputClassName = (fieldName: string, additionalClasses: string = '') => {
    const baseClass = "w-full px-4 py-3 rounded-lg border focus:ring-1 outline-none transition-colors";
    const heightClass = fieldName === 'reasonForAppeal' ? '' : 'h-[50px]';
    const hasError = fieldErrors[fieldName];
    const errorClass = hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-400"
      : "border-gray-300 focus:border-gray-400 focus:ring-gray-400";

    return `${baseClass} ${heightClass} ${errorClass} ${additionalClasses}`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    if (!formData.insuranceCardNumber.trim()) {
      errors.insuranceCardNumber = t.errors.insuranceCardNumber;
    }
    if (!formData.name.trim()) {
      errors.name = t.errors.name;
    }
    if (!formData.surname.trim()) {
      errors.surname = t.errors.surname;
    }
    if (!formData.birthDate) {
      errors.birthDate = t.errors.birthDate;
    }
    if (!formData.phone.trim()) {
      errors.phone = t.errors.phone;
    }
    if (!formData.email.trim()) {
      errors.email = t.errors.email;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t.errors.emailInvalid;
    }
    if (!formData.appealDate) {
      errors.appealDate = t.errors.appealDate;
    }
    if (!formData.appealTime) {
      errors.appealTime = t.errors.appealTime;
    }
    if (!formData.reasonForAppeal.trim()) {
      errors.reasonForAppeal = t.errors.reasonForAppeal;
    }
    if (!formData.wantsHospital) {
      errors.wantsHospital = t.errors.wantsHospital;
    }
    if (formData.wantsHospital === 'beli') {
      if (!formData.clinic) {
        errors.clinic = t.errors.clinic;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }

      Swal.fire({
        icon: 'warning',
        title: t.warningTitle,
        text: t.fillAllFields,
        confirmButtonText: t.close,
        confirmButtonColor: '#db2777'
      });
      return;
    }

    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: t.errorTitle,
        text: t.userNotFound,
        confirmButtonText: t.ok,
        confirmButtonColor: '#db2777',
      });
      return;
    }

    const appointmentDateTime = `${formData.appealDate}T${formData.appealTime}:00`;

    const requestData: any = {
      policy_number: formData.insuranceCardNumber,
      full_name: `${formData.name} ${formData.surname}`.trim(),
      birth_date: formData.birthDate,
      phone_number: formData.phone,
      email: formData.email,
      complaint: formData.reasonForAppeal,
      appointment_date: appointmentDateTime,
      hospital_chooser: formData.wantsHospital === 'beli' ? 'SELF' : 'INSURER',
      status: 'PENDING',
      user: userId,
      platform: 'WEB' as const,
      ...(formData.wantsHospital === 'beli' && {
        hospital: formData.clinic ? parseInt(formData.clinic) : null,
        doctor_name: formData.doctorName
      })
    };

    dispatch(createDoctorAppointment({
      requestData,
      language: 'az'
    }) as any);
  };

  if (loading) {
    return (
      <div className=" flex  justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <h1 className="text-3xl font-semibold text-stone-800">
        {t.title}
      </h1>

      <div className="mx-auto">
        {/* Info Banner */}
        <p className="text-stone-600 leading-relaxed mb-6 text-base">
          {t.subtitle}
        </p>
      </div>

      <div className="mx-auto bg-white rounded-lg shadow-sm relative overflow-hidden">
        <form onSubmit={handleSubmit} className="p-4 md:p-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t.name}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClassName('name')}
              />
              {fieldErrors.name && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                {t.surname}
              </label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClassName('surname')}
              />
              {fieldErrors.surname && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.surname}</p>
              )}
            </div>

            <div>
              <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                {t.birthDate}
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="birthDate"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={getInputClassName('birthDate', 'text-left')} // text-center yerine text-left
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                />
              </div>
              {fieldErrors.birthDate && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.birthDate}</p>
              )}
            </div>
            <div>
              <label htmlFor="insuranceCardNumber" className="block text-sm font-medium text-gray-700 mb-2">
                {t.insuranceCardNumber}
              </label>
              <input
                type="text"
                id="insuranceCardNumber"
                name="insuranceCardNumber"
                value={formData.insuranceCardNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClassName('insuranceCardNumber')}
              />
              {fieldErrors.insuranceCardNumber && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.insuranceCardNumber}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {t.phone}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={(e) => {
                  if (!formData.phone || formData.phone === "") {
                    setFormData(prev => ({ ...prev, phone: "+994 " }));
                    setTimeout(() => {
                      e.target.setSelectionRange(5, 5);
                    }, 0);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  const digits = pastedText.replace(/\D/g, '');

                  if (digits.length >= 9) {
                    const last9Digits = digits.slice(-9);
                    setFormData(prev => ({ ...prev, phone: `+994 ${last9Digits}` }));
                  } else if (digits.length > 0) {
                    const currentDigits = formData.phone.slice(5).replace(/\D/g, '');
                    const newDigits = (currentDigits + digits).slice(0, 9);
                    setFormData(prev => ({ ...prev, phone: `+994 ${newDigits}` }));
                  }
                }}
                className={getInputClassName('phone')}
              />
              {fieldErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t.email}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClassName('email')}
              />
              {fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            {/* Müraciət tarixi */}
            <input
              type="date"
              id="appealDate"
              name="appealDate"
              value={formData.appealDate}
              onChange={handleChange}
              onBlur={handleBlur}
              min={minDate}
              className={getInputClassName('appealDate', 'text-left')} // text-center yerine text-left
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none'
              }}
            />

            {/* Müraciət saatı */}
            <input
              type="time"
              id="appealTime"
              name="appealTime"
              value={formData.appealTime}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClassName('appealTime', 'text-left')} // text-center yerine text-left
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none'
              }}
            />
            <div>
              <label htmlFor="wantsHospital" className="block text-sm font-medium text-gray-700 mb-2">
                {t.wantsHospital}
              </label>
              <div className="relative">
                <select
                  id="wantsHospital"
                  name="wantsHospital"
                  value={formData.wantsHospital}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={getInputClassName('wantsHospital', 'appearance-none')}
                >
                  <option value="">{t.select}</option>
                  <option value="beli">{t.yes}</option>
                  <option value="xeyr">{t.no}</option>
                </select>
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {fieldErrors.wantsHospital && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.wantsHospital}</p>
              )}
            </div>

            {formData.wantsHospital === 'beli' && (
              <>
                <div>
                  <label htmlFor="clinic" className="block text-sm font-medium text-gray-700 mb-2">
                    {t.clinic}
                  </label>
                  <div className="relative">
                    <select
                      id="clinic"
                      name="clinic"
                      value={formData.clinic}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={hospitalsLoading}
                      className={getInputClassName('clinic', 'appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed')}
                    >
                      <option value="">
                        {hospitalsLoading ? t.loadingHospitals : t.select}
                      </option>
                      {hospitals.map((hospital: Hospital) => (
                        <option key={hospital.id} value={hospital.id}>
                          {hospital.name}
                        </option>
                      ))}
                    </select>
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {fieldErrors.clinic && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.clinic}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="doctorName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t.doctorName}
                  </label>
                  <input
                    type="text"
                    id="doctorName"
                    name="doctorName"
                    value={formData.doctorName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t.doctorNamePlaceholder}
                    className={getInputClassName('doctorName')}
                  />
                  {fieldErrors.doctorName && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.doctorName}</p>
                  )}
                </div>
                <div></div>
              </>
            )}

            <div className="md:col-span-3">
              <label htmlFor="reasonForAppeal" className="block text-sm font-medium text-gray-700 mb-2">
                {t.reasonForAppeal}
              </label>
              <textarea
                id="reasonForAppeal"
                name="reasonForAppeal"
                value={formData.reasonForAppeal}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t.reasonPlaceholder}
                rows={4}
                className={getInputClassName('reasonForAppeal', ' !h-[150px]')}
              />
              {fieldErrors.reasonForAppeal && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.reasonForAppeal}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full md:w-auto md:min-w-[200px] bg-colormainbrown hover:bg-colorbrownhover text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t.submitting : t.submit}
          </button>
        </form>
      </div>
      {/* Back Button */}
      <div className="mt-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-1 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-colorpink hover:text-white hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow font-medium"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">{t.back}</span>
        </button>
      </div>
    </div>
  );
};

export default Doctor_Appointment;